import createUrlBuilder from 'utilities/url.builder.utils';
import { getRequest } from 'utilities/service.utils';
import mapDataService from './mds.service.context';
import ArcGISWorldGeocodingService from './arcgisworldgeocoding.service.context';
import { MAP_DATA_SERVICE_DATA_SET } from 'types';
import { loadModules } from 'esri-loader';
import MapSettings from 'components/Map/settings';

const { REACT_APP_MAP_DATA_SERVICE_KEY } = process.env;
const { modules } = MapSettings;
const { baseMap, webMap } = modules;

export const getGeocodeByCoordinate = async (lon: string, lat: string) => {
  const accessKey = REACT_APP_MAP_DATA_SERVICE_KEY || '';
  const urlBuilder = createUrlBuilder('/geocodeByCoordinate');
  urlBuilder.addParameter('key', accessKey);
  urlBuilder.addParameter('datasets', MAP_DATA_SERVICE_DATA_SET.GEOCODEAU);
  urlBuilder.addParameter('latitude', lat);
  urlBuilder.addParameter('longitude', lon);
  return await getRequest(mapDataService, urlBuilder.toString());
};

export const getLotNumSuggest = async (
  query: string,
  fields: string,
  limit: string
) => {
  const accessKey = REACT_APP_MAP_DATA_SERVICE_KEY || '';
  const urlBuilder = createUrlBuilder('/place/suggest');
  urlBuilder.addParameter('key', accessKey);
  urlBuilder.addParameter('query', query);
  urlBuilder.addParameter('datasets', MAP_DATA_SERVICE_DATA_SET.GNAFLPID);
  urlBuilder.addParameter('fields', fields);
  urlBuilder.addParameter('limit', limit);
  return await getRequest(mapDataService, urlBuilder.toString());
};

export const getFeatureServerUrl = (
  service: string,
  publicMode: boolean = false
) => {
  return `${process.env.REACT_APP_BASE_URL}/geoservice${
    publicMode ? '' : '/internal'
  }/SmarterWX_Locate/rest/services/${service}/FeatureServer`;
};

export const featureRequestInterceptors = async (params: any) => {
  if (params?.requestOptions) {
    params.requestOptions.withCredentials = true;
  }
};

export const queryFeatuerLayerExtent = (
  featureLayer: __esri.FeatureLayer,
  query?: __esri.Query
) => {
  if (query) {
    return featureLayer.queryExtent(query);
  } else {
    return featureLayer.queryExtent();
  }
};

export const queryFeatuerLayerFeatures = (
  featureLayer: __esri.FeatureLayer,
  query: any = undefined
) => {
  return featureLayer.queryFeatures(query);
};

export const getBaseMaps = async (baseMaps: any[]) => {
  const modules = await loadModules([baseMap.namespace]);
  const Basemap = modules[0];
  const promises = baseMaps.map((map) => {
    const basemap = map.fromPortal
      ? new Basemap({
          portalItem: {
            id: map.id
          }
        })
      : Basemap.fromId(map.id);
    return basemap.load();
  });

  return await Promise.all(promises);
};

export const getCustomBaseMap = async (id: string) => {
  const modules = await loadModules([baseMap.namespace, webMap.namespace]);
  const Basemap = modules[0];
  const WebMap = modules[1];

  const customBasemap = new Basemap({ portalItem: { id } });
  await customBasemap.load();
  if (customBasemap.portalItem) {
    const webMap = new WebMap({ portalItem: { id } });
    await webMap.load();

    customBasemap.baseLayers.items = [
      ...customBasemap.baseLayers.items,
      ...webMap.layers.items
    ];
  }

  return customBasemap;
};

export const getAddressSuggest = async (
  text: string,
  category?: string,
  maxSuggestions?: number,
  location?: any
) => {
  const urlBuilder = createUrlBuilder('/suggest');
  urlBuilder.addParameter('text', text);
  urlBuilder.addParameter('f', 'pjson');
  urlBuilder.addParameter(
    'countryCode',
    process.env.REACT_APP_ARCGIS_WORLD_GEOCODING_SERVICE_BASE_COUNTRY || ''
  );

  urlBuilder.addParameter(
    'category',
    category
      ? category
      : process.env.REACT_APP_ARCGIS_WORLD_GEOCODING_SERVICE_BASE_CATEGORY || ''
  );

  urlBuilder.addParameter(
    'maxSuggestions',
    maxSuggestions
      ? maxSuggestions
      : process.env.REACT_APP_ARCGIS_WORLD_GEOCODING_SERVICE_BASE_LIMIT || ''
  );

  location && urlBuilder.addParameter('location', location);

  return getRequest(ArcGISWorldGeocodingService, urlBuilder.toString());
};

export const getAddressCandidate = async (
  text: string,
  outFields: string = '*',
  magicKey?: string
) => {
  const urlBuilder = createUrlBuilder('/findAddressCandidates');
  urlBuilder.addParameter('SingleLine', text);
  urlBuilder.addParameter('outFields', outFields);
  urlBuilder.addParameter('f', 'pjson');
  urlBuilder.addParameter(
    'countryCode',
    process.env.REACT_APP_ARCGIS_WORLD_GEOCODING_SERVICE_BASE_COUNTRY || ''
  );
  urlBuilder.addParameter('token', process.env.REACT_APP_ESRI_API_KEY || '');
  magicKey && urlBuilder.addParameter('magicKey', magicKey);

  return getRequest(ArcGISWorldGeocodingService, urlBuilder.toString());
};
